/*
 * <hdt-cart-recommendations>
 *
 * Fetches Shopify's complementary product recommendations for every product
 * currently in the cart, dedupes the aggregate, filters out items already in
 * the cart, and injects the result into the cart slider.
 *
 * Attributes:
 *   data-product-ids        Comma-separated cart product IDs to query
 *   data-cart-product-ids   Comma-separated cart product IDs to exclude
 *   data-limit              Max slides to render (default 10)
 *   data-section-id         Section to fetch (default cart-recommendations-content)
 *   data-intent             Recommendation intent (default complementary)
 *
 * Falls back to whatever is already rendered server-side when the API returns
 * nothing (manual product list / brand metafield).
 */
(function () {
  if (customElements.get('hdt-cart-recommendations')) return;

  class HdtCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this._fetched = false;
    }

    connectedCallback() {
      if (this._fetched) return;
      this._fetched = true;
      this.run();
    }

    async run() {
      const productIds = (this.dataset.productIds || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      if (productIds.length === 0) return;

      const cartIds = new Set(
        (this.dataset.cartProductIds || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      );
      const limit = parseInt(this.dataset.limit, 10) || 10;
      const sectionId = this.dataset.sectionId || 'cart-recommendations-content';
      const primaryIntent = this.dataset.intent || 'complementary';
      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      const ctx = { productIds, cartIds, limit, sectionId, root };

      // Cascade: try the primary intent first (default: complementary, which uses
      // Shopify Search & Discovery's manual curation). If empty, fall back to
      // `related` which uses Shopify's automatic algorithm (sales-based). This way
      // stores without S&D configured still get smart, data-driven suggestions.
      let slidesHtml = '';
      try {
        slidesHtml = await this.fetchAggregated({ ...ctx, intent: primaryIntent });
        if (!slidesHtml && primaryIntent !== 'related') {
          slidesHtml = await this.fetchAggregated({ ...ctx, intent: 'related' });
        }
      } catch (e) {
        return; // keep fallback
      }

      if (!slidesHtml) return; // keep fallback

      this.replaceSlider(slidesHtml);
    }

    async fetchAggregated({ productIds, cartIds, limit, sectionId, intent, root }) {
      const results = await Promise.all(
        productIds.map((id) => {
          const url = `${root}recommendations/products?section_id=${encodeURIComponent(
            sectionId
          )}&product_id=${encodeURIComponent(id)}&intent=${encodeURIComponent(
            intent
          )}&limit=${encodeURIComponent(limit)}`;
          return fetch(url, { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.text() : ''))
            .catch(() => '');
        })
      );

      const parser = new DOMParser();
      const seen = new Set();
      const slides = [];

      for (const html of results) {
        if (!html) continue;
        const doc = parser.parseFromString(html, 'text/html');
        const items = doc.querySelectorAll(
          '.hdt-minicart-recommendations__item[data-product-handle]'
        );
        for (const it of items) {
          const handle = it.getAttribute('data-product-handle');
          const id = it.getAttribute('data-product-id');
          if (!handle || seen.has(handle)) continue;
          if (id && cartIds.has(id)) continue;
          seen.add(handle);
          slides.push(it.outerHTML);
          if (slides.length >= limit) break;
        }
        if (slides.length >= limit) break;
      }

      return slides.length > 0 ? slides.join('') : '';
    }

    replaceSlider(slidesHtml) {
      const root = this.querySelector('[data-recommendations-root]');
      if (!root) return;

      const heading = this.dataset.heading || '';
      const sectionInstanceId = this.dataset.sectionInstanceId || '';
      const sliderId = `Slider-${sectionInstanceId}`;
      const showNav = this.dataset.showNav !== 'false';
      const showDots = this.dataset.showDots !== 'false';

      let navHtml = '';
      if (showNav) {
        const prevSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none"><path d="M5.5 11L0 5.5L5.5 0L6.47625 0.97625L1.9525 5.5L6.47625 10.0238L5.5 11Z" fill="currentColor"/></svg>';
        const nextSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="7" height="11" viewBox="0 0 7 11" fill="none"><path d="M1.5 11L7 5.5L1.5 0L0.52375 0.97625L5.0475 5.5L0.52375 10.0238L1.5 11Z" fill="currentColor"/></svg>';
        navHtml =
          '<div class="hdt-minicart-recommendations__nav no-js-hidden">' +
          `<button aria-controls="${sliderId}" class="hdt-slider__button hdt-show-all hdt-inline-flex hdt-align-center hdt-justify-center hdt-slider__button--prev" type="button" name="previous">${prevSvg}</button>` +
          `<button aria-controls="${sliderId}" class="hdt-slider__button hdt-show-all hdt-inline-flex hdt-align-center hdt-justify-center hdt-slider__button--next" type="button" name="next">${nextSvg}</button>` +
          '</div>';
      }

      const dotsHtml = showDots
        ? `<div hdt-slider-dots aria-controls="${sliderId}" class="hdt-minicart-recommendation__dots hdt-slider__dots hdt-slider__dots_outline hdt-flex hdt-justify-center no-js-hidden"></div>`
        : '';

      const sliderConfig = `{ "navUI": ${showNav}, "dotUI": ${showDots} }`;

      // Rebuild heading + slider + dots together so both custom elements
      // (hdt-slider and the dots component) re-initialize from scratch.
      root.innerHTML =
        '<div class="hdt-minicart-recommendations-heading">' +
        `<h3 class="hdt-minicart-recommendations__title hdt-s-text hdt-text-lg hdt-font-semibold">${heading}</h3>` +
        navHtml +
        '</div>' +
        `<hdt-slider id="${sliderId}" class="hdt-minicart-recommendations__wrap hdt-slider lg:hdt-slides-1 hdt-relative hdt-oh" config='${sliderConfig}'>` +
        '<div class="hdt-slider__viewport">' +
        '<div class="hdt-slider__container">' +
        slidesHtml +
        '</div></div></hdt-slider>' +
        dotsHtml;

      root.removeAttribute('hidden');
    }
  }

  customElements.define('hdt-cart-recommendations', HdtCartRecommendations);
})();
